import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { X as XIcon, FileText as FileTextIcon } from "lucide-react";

/*
 * Every draft is saved under the merchant's order reference: it is how the draft is found on Draft Orders and what
 * the shipment is booked under, and it keeps half-typed junk out of the list. The reference field lives on the
 * Payment & items step, so when Save draft is pressed before it is filled, this asks for it right here instead of
 * sending the merchant to another step.
 */
export default function DraftReferencePrompt({ open, initialValue = "", onCancel, onSave, variant = "forward" }) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);
  // The callbacks are fresh closures on every parent render; keep them in refs so opening is the only reset.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const initialRef = useRef(initialValue);
  initialRef.current = initialValue;

  useEffect(() => {
    if (!open) return undefined;
    setValue(initialRef.current);
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    const onKey = (e) => { if (e.key === "Escape") onCancelRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => { window.clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [open]);

  if (!open) return null;

  const trimmed = value.trim();
  const tooLong = trimmed.length > 50;
  const canSave = trimmed !== "" && !tooLong;
  const submit = (e) => { e.preventDefault(); if (canSave) onSave(trimmed); };
  const thing = variant === "return" ? "return" : "shipment";

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="draft-ref-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <form onSubmit={submit} className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl animate-scale-in">
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          title="Close (Esc)"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
        >
          <XIcon className="h-4 w-4" />
        </button>
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#3877FC]/10 text-[#2A59BD]">
          <FileTextIcon className="h-6 w-6" />
        </div>
        <h3 id="draft-ref-title" className="mt-4 text-[17px] font-semibold tracking-[-0.01em] text-slate-900">Give this draft your order reference</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-slate-500">
          Every draft is saved under an order reference. It is how you find it on Draft Orders and what the {thing} is booked under.
        </p>
        <label htmlFor="draft-ref-input" className="mt-4 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Order reference</label>
        <input
          id="draft-ref-input"
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={60}
          autoComplete="off"
          spellCheck={false}
          placeholder="e.g. SP-C-4560775"
          className={`mt-1.5 h-11 w-full rounded-lg border px-3 font-mono text-[13px] text-slate-900 outline-none transition-colors focus:border-[#3877FC] focus:ring-2 focus:ring-[#3877FC]/25 ${tooLong ? "border-rose-300 bg-rose-50/40" : "border-slate-200 bg-white"}`}
        />
        <p className={`mt-1.5 text-[12px] ${tooLong ? "text-rose-600" : "text-slate-400"}`}>
          {tooLong ? "Keep it to 50 characters." : "Your own order number works best. You can change it later on the Payment & items step."}
        </p>
        <div className="mt-5 flex gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-[13.5px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="inline-flex h-10 flex-1 items-center justify-center rounded-lg bg-[#3877FC] px-3 text-[13.5px] font-semibold text-white shadow-sm transition-all hover:bg-[#2A59BD] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/40"
          >
            Save draft
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
